import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import config from '../../config';

const EmailActivityDashboard = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await axios.get(`${config.API_BASE_URL}/analysis/email-activity`);

                // Parse and sort locally if needed, but backend already sends reversed (newest first)
                setLogs(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch email logs:', err);
                setError('Failed to load email activity logs.');
                setLoading(false);
            }
        };

        fetchLogs();
    }, []);

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleString('fr-FR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    const getModeColor = (mode) => {
        switch (mode?.toLowerCase()) {
            case 'direct': return 'success';
            case 'multiple': return 'info';
            case 'blocked': return 'error';
            default: return 'default';
        }
    };

    const formatTemplate = (template) => {
        if (!template) return '-';
        return template.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
                Journal d'Activité Email ({logs.length} entrées)
            </Typography>

            <TableContainer component={Paper} elevation={2}>
                <Table sx={{ minWidth: 650 }} aria-label="email activity table">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell><strong>Date & Heure</strong></TableCell>
                            <TableCell><strong>Utilisateur</strong></TableCell>
                            <TableCell><strong>Fournisseur</strong></TableCell>
                            <TableCell><strong>Mode d'Envoi</strong></TableCell>
                            <TableCell><strong>Modèle Utilisé</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.length > 0 ? (
                            logs.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#fcfcfc' } }}
                                >
                                    <TableCell component="th" scope="row">
                                        {formatDate(row.request_date)}
                                    </TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>
                                        {row.supplier_name ? (
                                            <Typography variant="body2" fontWeight="bold">
                                                {row.supplier_name}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                N/A
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.mode}
                                            color={getModeColor(row.mode)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {row.template_type ? (
                                            <Chip
                                                label={formatTemplate(row.template_type)}
                                                size="small"
                                                sx={{ maxWidth: 250 }}
                                            />
                                        ) : '-'}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    Aucune activité enregistrée.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default EmailActivityDashboard;
